/**
 * Question List Component
 *
 * Displays the list of questions in the form builder.
 * Supports drag-and-drop reordering with react-dnd.
 */

import { useCallback, useState } from 'react';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { FileQuestion, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { FormQuestion, DeviceMode } from './hooks/useFormBuilder';
import { QuestionCard } from './QuestionCard';
import { QuestionEditor } from './QuestionEditor';
import { QuestionTypeSelector, QuestionTypeSelectorInline } from './QuestionTypeSelector';
import { QuestionType, BranchingRule, QuestionOption } from '@/lib/forms/question-types';

const ITEM_TYPE = 'QUESTION';

interface DragItem {
  index: number;
  id: string;
  type: string;
}

interface QuestionListProps {
  questions: FormQuestion[];
  allQuestions: FormQuestion[]; // Unfiltered for branching targets
  selectedQuestionId: string | null;
  deviceMode: DeviceMode;
  enabledQuestionIds: string[];
  onSelect: (id: string | null) => void;
  onReorder: (startIndex: number, endIndex: number) => void;
  onUpdate: (id: string, updates: Partial<FormQuestion>) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onToggleVisibility: (id: string, device?: DeviceMode) => void;
  onAddQuestion: (type: QuestionType) => void;
  onAddOption: (questionId: string) => void;
  onUpdateOption: (questionId: string, optionId: string, updates: Partial<QuestionOption>) => void;
  onDeleteOption: (questionId: string, optionId: string) => void;
  onAddBranchingRule: (questionId: string, rule: BranchingRule) => void;
  onUpdateBranchingRule: (questionId: string, index: number, updates: Partial<BranchingRule>) => void;
  onDeleteBranchingRule: (questionId: string, index: number) => void;
  isQuizMode?: boolean; // Enable quiz-specific question editing
  className?: string;
}

export function QuestionList(props: QuestionListProps) {
  return (
    <DndProvider backend={HTML5Backend}>
      <QuestionListInner {...props} />
    </DndProvider>
  );
}

function QuestionListInner({
  questions,
  allQuestions,
  selectedQuestionId,
  deviceMode,
  enabledQuestionIds,
  onSelect,
  onReorder,
  onUpdate,
  onDelete,
  onDuplicate,
  onToggleVisibility,
  onAddQuestion,
  onAddOption,
  onUpdateOption,
  onDeleteOption,
  onAddBranchingRule,
  onUpdateBranchingRule,
  onDeleteBranchingRule,
  isQuizMode,
  className,
}: QuestionListProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const moveQuestion = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      if (dragIndex !== hoverIndex) {
        onReorder(dragIndex, hoverIndex);
      }
    },
    [onReorder]
  );

  // Empty state
  if (questions.length === 0) {
    return (
      <div className={cn('space-y-6', className)}>
        <Card className="text-center py-12">
          <FileQuestion className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No questions yet</h3>
          <p className="text-muted-foreground mb-6">
            Get started by adding your first question
          </p>
          <QuestionTypeSelectorInline onSelect={onAddQuestion} />
        </Card>
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {questions.map((question, index) => (
        <DraggableQuestionCard
          key={question.id}
          question={question}
          allQuestions={allQuestions}
          index={index}
          isSelected={selectedQuestionId === question.id}
          isExpanded={expandedIds.has(question.id)}
          deviceMode={deviceMode}
          isVisibleOnDevice={enabledQuestionIds.includes(question.id)}
          onSelect={() => onSelect(question.id)}
          onToggleExpand={() => toggleExpanded(question.id)}
          onDelete={() => onDelete(question.id)}
          onDuplicate={() => onDuplicate(question.id)}
          onToggleVisibility={(device) => onToggleVisibility(question.id, device)}
          onUpdate={(updates) => onUpdate(question.id, updates)}
          onAddOption={() => onAddOption(question.id)}
          onUpdateOption={(optionId, updates) => onUpdateOption(question.id, optionId, updates)}
          onDeleteOption={(optionId) => onDeleteOption(question.id, optionId)}
          onAddBranchingRule={(rule) => onAddBranchingRule(question.id, rule)}
          onUpdateBranchingRule={(idx, updates) => onUpdateBranchingRule(question.id, idx, updates)}
          onDeleteBranchingRule={(idx) => onDeleteBranchingRule(question.id, idx)}
          moveQuestion={moveQuestion}
        />
      ))}

      {/* Add Question Button */}
      <div className="pt-2">
        <QuestionTypeSelector onSelect={onAddQuestion} />
      </div>
    </div>
  );
}

interface DraggableQuestionCardProps {
  question: FormQuestion;
  allQuestions: FormQuestion[];
  index: number;
  isSelected: boolean;
  isExpanded: boolean;
  deviceMode: DeviceMode;
  isVisibleOnDevice: boolean;
  onSelect: () => void;
  onToggleExpand: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onToggleVisibility: (device?: DeviceMode) => void;
  onUpdate: (updates: Partial<FormQuestion>) => void;
  onAddOption: () => void;
  onUpdateOption: (optionId: string, updates: Partial<QuestionOption>) => void;
  onDeleteOption: (optionId: string) => void;
  onAddBranchingRule: (rule: BranchingRule) => void;
  onUpdateBranchingRule: (index: number, updates: Partial<BranchingRule>) => void;
  onDeleteBranchingRule: (index: number) => void;
  moveQuestion: (dragIndex: number, hoverIndex: number) => void;
}

function DraggableQuestionCard({
  question,
  allQuestions,
  index,
  isSelected,
  isExpanded,
  deviceMode,
  isVisibleOnDevice,
  onSelect,
  onToggleExpand,
  onDelete,
  onDuplicate,
  onToggleVisibility,
  onUpdate,
  onAddOption,
  onUpdateOption,
  onDeleteOption,
  onAddBranchingRule,
  onUpdateBranchingRule,
  onDeleteBranchingRule,
  moveQuestion,
}: DraggableQuestionCardProps) {
  const [{ isDragging }, drag, preview] = useDrag({
    type: ITEM_TYPE,
    item: (): DragItem => ({ id: question.id, index, type: ITEM_TYPE }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: ITEM_TYPE,
    hover(item: DragItem, monitor) {
      if (!monitor.isOver({ shallow: true })) return;
      if (item.index === index) return;
      moveQuestion(item.index, index);
      item.index = index;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
  });

  return (
    <div ref={drop} className={cn(isOver && 'opacity-50')}>
      <QuestionCard
        ref={preview}
        question={question}
        index={index}
        isSelected={isSelected}
        isExpanded={isExpanded}
        deviceMode={deviceMode}
        isVisibleOnDevice={isVisibleOnDevice}
        onSelect={onSelect}
        onToggleExpand={onToggleExpand}
        onDelete={onDelete}
        onDuplicate={onDuplicate}
        onToggleVisibility={onToggleVisibility}
        onInlineEdit={(field, value) => {
          if (field === 'text') onUpdate({ text: value });
        }}
        dragHandleProps={{ ref: drag }}
        isDragging={isDragging}
      >
        {isExpanded && (
          <QuestionEditor
            question={question}
            allQuestions={allQuestions}
            onUpdate={onUpdate}
            onAddOption={onAddOption}
            onUpdateOption={onUpdateOption}
            onDeleteOption={onDeleteOption}
            onAddBranchingRule={onAddBranchingRule}
            onUpdateBranchingRule={onUpdateBranchingRule}
            onDeleteBranchingRule={onDeleteBranchingRule}
          />
        )}
      </QuestionCard>
    </div>
  );
}
